// ControlId → transport event (P2.8) — FR-39's "mapping is data, not code".
//
// The mapping layer for the transport half of the control plane. Architecture §3 splits the bus at
// the point of consumption, and this is the structural side of that split: play and cue are
// discrete, so they become `TransportEvent`s over postMessage rather than ring writes.
//
// **The continuous side of that split is `/audio/engine/control-mapping.ts`.** It held a table from
// P0.6 to P2.12 — ControlId → EngineCommand → ring — but every entry in it was a tone-generator
// opcode (a frequency in Hz, a gain against the tone's ceiling), so it retired with the tone
// generator rather than being rewritten into something no caller had asked for. **P3.6 brought it
// back** with FR-05's pitch fader, the first control that streams. From P2.12 to P3.6 this file was
// the whole of FR-39's seam; now the two halves are a pair, and a consumer offers each event to both
// tables knowing at most one will claim it.
//
// **A table, checked by a test, because P2.8's VERIFY asks for exactly that** — "the mapping table
// is data, not a switch statement (FR-39's seam)". The distinction is not cosmetic: FR-39 ships a
// remapping UI with persisted user mappings, so this table has to be something a user can edit and
// something that can be serialised. A `switch` is neither.
//
// Source-independent on purpose. A pointer press, a Space keydown and (at FF-1) a MIDI note-on all
// arrive here as the same `deck.a.play` at value 1, and none of them gets a private route.
//
// **Deck-independent too, since P3.7.** `deck.b.play` and `deck.a.play` mean the same thing to the
// transport, so they are the same row twice rather than two rows — which is what "the ids carry the
// deck" was for. The subscriber that receives the event is the one that knows which deck it is.

import { DECK_IDS, type ControlId, type DeckId } from '../platform/types/control';
import type { TransportEvent } from '../platform/types/transport';

/**
 * The transport controls, named in Architecture §3's namespace grammar, **per deck** (P3.7).
 *
 * ⚠️ **The table below needed exactly one more row per deck, as P2.8 promised. This constant needed
 * a shape change, which P2.8 did not.** It was written flat — `TRANSPORT_CONTROL.PLAY` — because
 * with one deck there was nothing to index by, and every caller reads it that way. That is a small
 * cost and it is worth naming rather than absorbing: an id constant that hard-codes a deck is one
 * more place the deck has to be threaded, and the ids themselves never had the problem because they
 * carried the deck from P0.2. `control-mapping.ts` was one row short in exactly the same way.
 *
 * Indexed by `DeckId` so it stays exhaustive by construction: a third deck would be a type error at
 * every use rather than a row somebody forgot.
 */
export const TRANSPORT_CONTROL = {
  a: { PLAY: 'deck.a.play', CUE: 'deck.a.cue' },
  b: { PLAY: 'deck.b.play', CUE: 'deck.b.cue' },
} as const satisfies Record<DeckId, Record<string, ControlId>>;

interface TransportMapping {
  /** What a press means. Every transport control has one. */
  readonly onPress: TransportEvent;
  /**
   * What a release means, or null when the control is a trigger rather than a hold.
   *
   * This one field is the whole difference between play and cue, and it is data rather than a
   * branch: play is a trigger (release means nothing — `phase` already flipped), cue is a hold
   * (release is *the* interesting half, because release-returns is what FR-02 is about).
   */
  readonly onRelease: TransportEvent | null;
}

/**
 * The table.
 *
 * A `Map`, not an object literal, for the reason `control-mapping.ts` already documents and which
 * matters more here: FR-39 makes control ids **user data**, and `{}['constructor']` is a function
 * rather than undefined — so an object-literal lookup sails past its own `undefined` check and
 * crashes on the property access. A Map has no inherited keys.
 */
const TRANSPORT_MAPPINGS = new Map<string, TransportMapping>(
  // Built by walking `DECK_IDS` rather than by listing four rows, because the four rows are two
  // rows twice and a hand-written fourth is the one that gets forgotten. What a control *means* is
  // still data — the two entries below are the table; the loop only says it holds for both decks.
  DECK_IDS.flatMap((deck): [string, TransportMapping][] => [
    [TRANSPORT_CONTROL[deck].PLAY, { onPress: 'play-pressed', onRelease: null }],
    [TRANSPORT_CONTROL[deck].CUE, { onPress: 'cue-pressed', onRelease: 'cue-released' }],
  ]),
);

/** Every control the transport answers to. Exported so a cheat sheet (FR-40) and a remapping UI
 * (FR-39) can enumerate them rather than hard-coding a list that drifts. */
export const TRANSPORT_CONTROL_IDS = [
  ...TRANSPORT_MAPPINGS.keys(),
] as readonly ControlId[];

/**
 * The transport event for a control at a given value, or null if there is nothing to do.
 *
 * `value` is `ControlEvent`'s normalised 0..1: 1 is pressed, 0 is released. Momentary controls only
 * ever carry those two, and treating anything above zero as pressed is what lets a MIDI note's
 * velocity arrive at FF-1 without this table caring.
 *
 * Null is ordinary rather than an error, in two ways: a control this table does not know (the pitch
 * fader lives in the other table), and a release of a trigger control (releasing Play means
 * nothing). Both are the bus working as §3 designed it — plenty of events cross the control plane
 * and never reach the audio one.
 */
export function toTransportEvent(id: ControlId, value: number): TransportEvent | null {
  const mapping = TRANSPORT_MAPPINGS.get(id);
  if (mapping === undefined) {
    return null;
  }
  return value > 0 ? mapping.onPress : mapping.onRelease;
}

/** Does this control belong to the transport at all? For a UI that wants to show which keys are
 * live without duplicating the table. */
export function isTransportControl(id: ControlId): boolean {
  return TRANSPORT_MAPPINGS.has(id);
}
